import { getTabIndex } from '@/datalayer/App.datalayer.utils';
import { mapTargetBySelectors } from '@/features/shared/HOCs/withGTM';
import { OnDatalayerProps } from '@/features/shared/HOCs/withGTM.interface';
import { pushClickToDL, TabsDLClick } from '../ComplexTabsList.datalayer';

enum Selectors {
  // Target
  Scroll = '.gtm-scroll',
  CTA = 'a',
  Card = '.swiper-slide',
  Tab = '.gtm-tab',
  // Content
  TabTitle = '.heading',
  CardHeading = '.gtm-headline',
  SlideActive = '.swiper-slide-active .gtm-tab-title',
  CTAheadline = '.gtm-headline:not([hidden])',
  CardTitle = '.gtm-tab-title',
}

export const mapTarget = mapTargetBySelectors([
  Selectors.Scroll,
  Selectors.Tab,
  Selectors.Card,
  Selectors.CTA,
]);

export const onDatalayerScroll = ({ target }: OnDatalayerProps) => {
  const { tabTitle, variantName = '' } = target;

  if (!target) {
    return undefined;
  }

  return {
    event: 'scrollCarousel',
    eventCategory: 'Complex Tab',
    ...(!!tabTitle && { tabTitle }),
    ...(!!variantName && { variantName }),
  };
};

export const onDatalayer = ({ componentRef, target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const variantName = (target?.closest('[data-datalayer-variant]') as HTMLDivElement)?.dataset
    ?.datalayerVariant;

  const isCard = target?.classList.contains(Selectors.Card.replace('.', ''));
  const isTab = target?.classList.contains(Selectors.Tab.replace('.', ''));
  const isCTA = target?.tagName?.toLowerCase() === Selectors.CTA;

  if (isTab) {
    const documentHeadline = target?.textContent || '';

    return {
      event: 'clickTab',
      eventCategory: 'Complex Tab',
      ...(!!documentHeadline && { documentHeadline }),
      ...(!!documentHeadline && { tabLabel: documentHeadline }),
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isCard) {
    const documentHeadline = target.querySelector(Selectors.CardTitle)?.textContent || '';

    return {
      event: 'clickCard',
      eventCategory: 'Complex Tab',
      ...(!!documentHeadline && { documentHeadline }),
      ...(variantName && { variant_name: variantName }),
    };
  }

  if (isCTA) {
    const documentHeadline =
      componentRef?.current?.querySelector(Selectors.CTAheadline)?.textContent || '';

    return {
      event: 'clickCTA',
      eventCategory: 'Complex Tab',
      ...(!!documentHeadline && { documentHeadline }),
      ...(variantName && { variant_name: variantName }),
    };
  }

  return undefined;
};

const getActiveTabPanel = (
  target: any,
  componentRef: OnDatalayerProps['componentRef'],
): HTMLElement | undefined | null => {
  const carouselId = target.closest('.swiper-container').id;
  const activeTabIndex = getTabIndex(carouselId) - 1;
  return componentRef?.current?.querySelector(
    ` #vertical-with-carousel-tabpanel-${activeTabIndex}`,
  );
};

const getTitle = (
  target: HTMLAnchorElement | HTMLElement,
  componentRef: OnDatalayerProps['componentRef'],
): string => {
  const isDot = target?.classList.contains(Selectors.Scroll.replace('.', ''));
  if (isDot) {
    const activeTabPanel = getActiveTabPanel(target, componentRef);
    return activeTabPanel?.querySelector(Selectors.CardHeading)?.children[0].textContent || '';
  }
  const isTab = target?.classList.contains(Selectors.Tab.replace('.', ''));
  const isLink = target?.tagName?.toLowerCase() === Selectors.CTA;

  let heading = '';
  if (isTab) {
    heading = target.querySelector(Selectors.TabTitle)?.children[0]?.textContent || '';
  }
  if (isLink) {
    const parent = target.closest('div[role=tabpanel]');
    heading = parent?.querySelector(Selectors.CardHeading)?.children[0]?.textContent || '';
  }
  return heading;
};

export const onDatalayerTabClick = ({
  componentRef,
  target,
}: OnDatalayerProps): TabsDLClick | undefined => {
  const title = getTitle(target, componentRef);
  return pushClickToDL({ target, title });
};
